/*
 * Вкладка Security сущности: находки DefectDojo кластера по продукту сервиса (путь проекта
 * GitLab). Отчёты приносит джоба app:defectdojo компонента app-security (gitleaks, semgrep,
 * trivy fs/image): сводка по серьёзности, последние прогоны сканеров, таблица активных находок
 * со ссылками в DefectDojo.
 */
import { Entity } from '@backstage/catalog-model';
import {
  EmptyState,
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Box, Chip, Grid, Typography, makeStyles } from '@material-ui/core';
import useAsync from 'react-use/esm/useAsync';
import { Finding, ProductFindings, defectdojoApiRef } from '../../lib/defectdojo';
import { DATASTORE_CLUSTER_ANNOTATION, annotation } from '../../lib/viva';

const GITLAB_SLUG_ANNOTATION = 'gitlab.com/project-slug';
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];

export const securityProductOf = (entity: Entity) => ({
  cluster: annotation(entity, DATASTORE_CLUSTER_ANNOTATION),
  product: annotation(entity, GITLAB_SLUG_ANNOTATION),
});

const useStyles = makeStyles(theme => ({
  Critical: { background: theme.palette.error.dark, color: theme.palette.error.contrastText },
  High: { background: theme.palette.error.main, color: theme.palette.error.contrastText },
  Medium: { background: theme.palette.warning.main, color: theme.palette.warning.contrastText },
  Low: { background: theme.palette.info.main, color: theme.palette.info.contrastText },
  Info: { background: theme.palette.grey[500], color: theme.palette.getContrastText(theme.palette.grey[500]) },
  chip: { fontWeight: 600, minWidth: 72 },
}));

const SeverityChip = ({ severity, count }: { severity: string; count?: number }) => {
  const classes = useStyles();
  const cls = (classes as Record<string, string>)[severity] ?? classes.Info;
  return (
    <Chip
      size="small"
      className={`${classes.chip} ${cls}`}
      label={count === undefined ? severity : `${severity}: ${count}`}
    />
  );
};

const Location = ({ finding: f }: { finding: Finding }) => {
  if (f.component) {
    return (
      <span>
        {f.component}
        {f.componentVersion ? ` ${f.componentVersion}` : ''}
      </span>
    );
  }
  if (f.filePath) {
    return (
      <code>
        {f.filePath}
        {f.line ? `:${f.line}` : ''}
      </code>
    );
  }
  return <span>—</span>;
};

const columns: TableColumn<Finding>[] = [
  {
    title: 'Серьёзность',
    field: 'severity',
    width: '120px',
    customSort: (a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity),
    render: f => <SeverityChip severity={f.severity} />,
  },
  {
    title: 'Находка',
    field: 'title',
    render: f => (
      <Box>
        <Link to={f.url}>{f.title}</Link>
        {f.vulnIds.length > 0 && (
          <Typography variant="caption" display="block" color="textSecondary">
            {f.vulnIds.join(', ')}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    title: 'Где',
    render: f => <Location finding={f} />,
  },
  { title: 'Сканер', field: 'test', width: '140px' },
  {
    title: 'Найдено',
    field: 'found',
    width: '120px',
    render: f => new Date(f.found).toLocaleDateString('ru-RU'),
  },
];

const Summary = ({ data }: { data: ProductFindings }) => (
  <InfoCard
    title="Сводка"
    subheader={
      data.product ? (
        <Link to={data.product.url}>{data.product.name} в DefectDojo</Link>
      ) : (
        'Продукт в DefectDojo ещё не создан'
      )
    }
  >
    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
      {SEVERITIES.map(s => (
        <SeverityChip key={s} severity={s} count={data.summary[s] ?? 0} />
      ))}
    </Box>
    {data.tests.length > 0 && (
      <Box mt={2}>
        <Typography variant="subtitle2">Последние прогоны сканеров</Typography>
        {data.tests.slice(0, 8).map(t => (
          <Typography key={t.id} variant="body2">
            <Link to={t.url}>{t.title}</Link> — {t.findings} акт.,{' '}
            {new Date(t.updated).toLocaleString('ru-RU')}
          </Typography>
        ))}
      </Box>
    )}
  </InfoCard>
);

export const SecurityContent = () => {
  const { entity } = useEntity();
  const api = useApi(defectdojoApiRef);
  const { cluster, product } = securityProductOf(entity);
  const { value, loading, error } = useAsync(
    async () => (cluster && product ? api.getFindings(cluster, product) : undefined),
    [cluster, product],
  );
  if (!cluster || !product) {
    return (
      <EmptyState
        missing="info"
        title="Нет привязки к DefectDojo"
        description={`Нужны аннотации ${DATASTORE_CLUSTER_ANNOTATION} и ${GITLAB_SLUG_ANNOTATION}`}
      />
    );
  }
  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;
  if (!value) return null;
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Summary data={value} />
      </Grid>
      <Grid item xs={12}>
        <Table
          title={`Активные находки (${value.findings.length})`}
          options={{ paging: value.findings.length > 25, pageSize: 25, search: true, padding: 'dense' }}
          columns={columns}
          data={value.findings}
          emptyContent={
            <Box p={3}>
              <Typography>
                Активных находок нет
                {value.product
                  ? ''
                  : ' — отчёты появятся после первого прогона пайплайна с проверками безопасности (app:defectdojo)'}
                .
              </Typography>
            </Box>
          }
        />
      </Grid>
    </Grid>
  );
};
